/**
 * Timed shifts: the grade and the best scores. Pure; storage is passed in.
 *
 * Grade (an ASSUMPTION, tuned for play): score per aircraft handled (landings + handoffs)
 * against the 10 points a clean one earns. 90 % or better A, 75 % B, 55 % C, 30 % D, else F;
 * no aircraft handled is an F, and any separation loss caps the grade at C.
 */
import type { Stats } from '@atc/sim';

export type Grade = 'A' | 'B' | 'C' | 'D' | 'F';

export interface ShiftRecord {
  position?: 'legacy' | 'tower';
  coaching?: boolean;
  airport: string;
  realism: string;
  traffic: string;
  minutes: number;
  score: number;
  grade: Grade;
  handled: number;
  /** ISO time the shift ended. */
  at: string;
}

export const SCORES_KEY = 'atc.scores.v2';
/** Best shifts kept per setup (airport, realism, traffic, length). */
export const KEEP_PER_SETUP = 10;

export function grade(s: Pick<Stats, 'score' | 'landings' | 'handoffs' | 'conflicts' | 'runwayConflicts' | 'collisions'>): Grade {
  if (s.collisions > 0) return 'F';
  const handled = s.landings + s.handoffs;
  if (handled === 0) return 'F';
  const r = s.score / (10 * handled);
  const g: Grade = r >= 0.9 ? 'A' : r >= 0.75 ? 'B' : r >= 0.55 ? 'C' : r >= 0.3 ? 'D' : 'F';
  if ((s.runwayConflicts ?? 0) > 0 && ['A', 'B', 'C'].includes(g)) return 'D';
  return s.conflicts > 0 && (g === 'A' || g === 'B') ? 'C' : g;
}

export const setupKey = (r: Pick<ShiftRecord, 'airport' | 'realism' | 'traffic' | 'minutes' | 'position' | 'coaching'>): string => `${r.airport}|${r.realism}|${r.traffic}|${r.minutes}${r.position === 'tower' ? `|tower-v1|${r.coaching !== false ? 'coached' : 'expert'}` : ''}`;

function isRecord(r: unknown): r is ShiftRecord {
  if (!r || typeof r !== 'object') return false;
  const o = r as Record<string, unknown>;
  return typeof o.airport === 'string' && typeof o.realism === 'string' && typeof o.traffic === 'string' && typeof o.minutes === 'number'
    && typeof o.score === 'number' && typeof o.grade === 'string' && typeof o.handled === 'number' && typeof o.at === 'string';
}

export function loadScores(storage: Pick<Storage, 'getItem'> | undefined): ShiftRecord[] {
  try {
    const raw: unknown = JSON.parse(storage?.getItem(SCORES_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter(isRecord) : [];
  } catch {
    return [];
  }
}

/**
 * Add a finished shift. Returns its rank among shifts with the same setup (1 = best), or null
 * when it did not make the top ten, and that top ten.
 */
export function recordScore(storage: Pick<Storage, 'getItem' | 'setItem'> | undefined, record: ShiftRecord): { rank: number | null; best: ShiftRecord[] } {
  const all = loadScores(storage);
  const key = setupKey(record);
  // Ties keep the earlier shift ahead.
  const best = [...all.filter((r) => setupKey(r) === key), record].sort((a, b) => b.score - a.score || a.at.localeCompare(b.at)).slice(0, KEEP_PER_SETUP);
  const idx = best.indexOf(record);
  try { storage?.setItem(SCORES_KEY, JSON.stringify([...all.filter((r) => setupKey(r) !== key), ...best])); } catch { /* blocked storage: the debrief still shows this shift */ }
  return { rank: idx >= 0 ? idx + 1 : null, best };
}
